
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import ParticleEffect from '../home/components/ParticleEffect';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface GalleryImage {
  id: string;
  title: string;
  description: string;
  image_url: string;
  created_at: string;
}

export default function GalleryPage() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  useEffect(() => {
    loadGalleryImages();
  }, []);

  const loadGalleryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('category', 'Gallery')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading gallery images:', error);
        setImages([]);
      } else {
        // Transform journal entries to gallery format
        const galleryData = (data || []).map(entry => ({
          id: entry.id,
          title: entry.title,
          description: entry.content,
          image_url: entry.thumbnail_url || '',
          created_at: entry.created_at
        })).filter(item => item.image_url); // Only show entries with images

        setImages(galleryData);
      }
    } catch (error) {
      console.error('Error:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (image: GalleryImage) => {
    setSelectedImage(image);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <ParticleEffect />
        <Navigation />
        <div className="pt-24 pb-16 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <div className="text-xl md:text-2xl font-light tracking-wide">
              Loading mystical visions...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <ParticleEffect />
      <Navigation />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light tracking-wider mb-6 text-transparent bg-gradient-to-r from-violet-400 to-cyan-300 bg-clip-text">
              Mystical Gallery
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-purple-200 max-w-2xl mx-auto leading-relaxed">
              A collection of sacred visions, mystical symbols, and ethereal manifestations from the collective consciousness.
            </p>
          </div>

          {/* Gallery Grid */}
          {images.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {images.map((image) => (
                <div
                  key={image.id}
                  onClick={() => openImageModal(image)}
                  className="group bg-black/60 backdrop-blur-sm border border-purple-500/30 rounded-lg overflow-hidden hover:border-purple-400/50 transition-all duration-300 cursor-pointer hover:scale-105"
                >
                  <div className="aspect-square bg-gradient-to-br from-purple-900/40 to-blue-900/40 overflow-hidden">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1">{image.title}</h3>
                    <p className="text-purple-300 text-sm leading-relaxed line-clamp-2">{image.description}</p>
                    <div className="text-xs text-purple-400 mt-3">
                      {new Date(image.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center">
                <i className="ri-image-line text-3xl text-purple-300"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-purple-200">
                Gallery Awaits Your Visions
              </h3>
              <p className="text-purple-300 max-w-md mx-auto">
                Upload mystical images through your <a href="/journal" className="text-violet-400 hover:text-violet-300 underline cursor-pointer">Journal</a> to populate this sacred gallery.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full max-h-[95vh] overflow-auto">
            {/* Close Button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/80 backdrop-blur-sm border border-purple-500/50 rounded-full flex items-center justify-center text-white hover:bg-purple-600/30 transition-all duration-300"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Image Container */}
            <div className="bg-black/80 backdrop-blur-sm border border-purple-500/50 rounded-lg overflow-hidden">
              {/* Full Image Display - No Height Constraints */}
              <div className="flex justify-center items-start p-4">
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.title}
                  className="max-w-full h-auto rounded-lg shadow-2xl"
                  style={{ maxHeight: 'none' }}
                />
              </div>
              
              {/* Image Info */}
              <div className="p-6 border-t border-purple-500/30">
                <h2 className="text-2xl font-semibold text-white mb-3">{selectedImage.title}</h2>
                <p className="text-purple-200 leading-relaxed mb-4">{selectedImage.description}</p>
                <div className="text-sm text-purple-400">
                  Created on {new Date(selectedImage.created_at).toLocaleDateString()} at {new Date(selectedImage.created_at).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
